import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, timeAgo } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, LogOut, Sparkles, X, Home, FileText, Rss, Inbox, Newspaper, MessageSquare, Settings, Shield, Quote, Vote, Tag, PlayCircle, BarChart3, Megaphone } from "lucide-react";
import { AdminSourcesTab, AdminCapturedTab, AdminPagesTab, AdminContactTab } from "@/pages/admin/AdminTabs";
import { AdminCommentsTab, AdminSettingsTab } from "@/pages/admin/AdminTabs2";
import AdminOpinionTab from "@/pages/admin/AdminOpinionTab";
import AdminPollsTab from "@/pages/admin/AdminPollsTab";
import AdminUsersTab from "@/pages/admin/AdminUsersTab";
import AdminCatalogTab from "@/pages/admin/AdminCatalogTab";
import AdminShortsFeedTab from "@/pages/admin/AdminShortsFeedTab";
import AdminAnalyticsTab from "@/pages/admin/AdminAnalyticsTab";
import AdminAdsTab from "@/pages/admin/AdminAdsTab";
import RichEditor from "@/components/RichEditor";

const EMPTY = {
  title: "",
  volanta: "",
  bajada: "",
  body: "",
  category_slug: "actualidad",
  tags: [],
  featured_image: "",
  highlight_level: "normal",
  status: "published",
  is_short: false,
  is_breaking: false,
  video_provider: "tiktok",
  video_embed_url: "",
  duration: "",
  source_name: "",
  source_url: "",
  read_minutes: 2,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [articles, setArticles] = useState([]);
  const [articlesTotal, setArticlesTotal] = useState(0);
  const [articlesQuery, setArticlesQuery] = useState({ q: "", category: "", status: "", date_range: "" });
  const [articlesPage, setArticlesPage] = useState(0);
  const ARTICLES_PER_PAGE = 20;
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showCurator, setShowCurator] = useState(false);
  const [curatorPrefill, setCuratorPrefill] = useState(null);
  const [tab, setTab] = useState("articles");

  useEffect(() => {
    const token = localStorage.getItem("popu-token");
    if (!token) {
      navigate("/admin");
      return;
    }
    const u = localStorage.getItem("popu-user");
    if (u) setUser(JSON.parse(u));
    loadAll();
  }, [navigate]);

  const loadAll = async () => {
    try {
      const [aR, cR] = await Promise.all([
        api.get("/admin/articles", { params: {
          limit: ARTICLES_PER_PAGE,
          offset: articlesPage * ARTICLES_PER_PAGE,
          q: articlesQuery.q || undefined,
          category: articlesQuery.category || undefined,
          status_filter: articlesQuery.status || undefined,
          date_range: articlesQuery.date_range || undefined,
        }}),
        api.get("/categories"),
      ]);
      const data = aR.data;
      // Backwards-compat: support either {items,total} or raw array
      if (Array.isArray(data)) {
        setArticles(data);
        setArticlesTotal(data.length);
      } else {
        setArticles(data.items || []);
        setArticlesTotal(data.total || 0);
      }
      setCategories(cR.data || []);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("popu-token");
        navigate("/admin");
      } else {
        toast.error("Error cargando datos");
      }
    }
  };

  // Reload when filters or page change
  useEffect(() => {
    if (user) loadAll();
  }, [articlesPage, articlesQuery.q, articlesQuery.category, articlesQuery.status, articlesQuery.date_range]);

  const logout = () => {
    localStorage.removeItem("popu-token");
    localStorage.removeItem("popu-user");
    navigate("/admin");
  };

  const openNew = () => {
    setEditing({ ...EMPTY });
    setShowForm(true);
  };
  const openEdit = (a) => {
    setEditing({ ...a, tags: a.tags || [] });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing };
      if (typeof payload.tags === "string") {
        payload.tags = payload.tags.split(",").map((s) => s.trim()).filter(Boolean);
      }
      if (editing.id) {
        await api.put(`/admin/articles/${editing.id}`, payload);
        toast.success("Artículo actualizado");
      } else {
        await api.post("/admin/articles", payload);
        toast.success("Artículo creado");
      }
      closeForm();
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`¿Eliminar "${a.title}"?`)) return;
    try {
      await api.delete(`/admin/articles/${a.id}`);
      toast.success("Eliminado");
      loadAll();
    } catch {
      toast.error("Error eliminando");
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <header className="border-b sticky top-0 z-30" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-go-home" className="flex items-center gap-2">
              <span className="popu-logo text-xl">POPU<span className="dot-news accent-text">.news</span></span>
              <span className="text-xs uppercase tracking-widest border-l pl-3 ml-2" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>
                Panel Editorial
              </span>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="btn-ghost flex items-center gap-1" data-testid="admin-link-home"><Home size={14} /> Sitio</Link>
            <span className="text-sm hidden md:inline" style={{ color: "var(--text-muted)" }}>{user?.email}</span>
            <button onClick={logout} className="btn-ghost flex items-center gap-1" data-testid="admin-logout">
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-8 overflow-x-auto no-scrollbar border-b" style={{ borderColor: "var(--border)" }}>
          {[
            { k: "articles", label: "Artículos", icon: Newspaper },
            { k: "opinion", label: "Opinión", icon: Quote },
            { k: "polls", label: "Encuestas", icon: Vote },
            { k: "catalog", label: "Categorías/Autores", icon: Tag },
            { k: "shortsfeed", label: "Shorts Feed", icon: PlayCircle },
            { k: "analytics", label: "Analytics", icon: BarChart3 },
            { k: "ads", label: "Publicidad", icon: Megaphone },
            { k: "captured", label: "Captura", icon: Inbox },
            { k: "sources", label: "Fuentes", icon: Rss },
            { k: "pages", label: "Páginas", icon: FileText },
            { k: "comments", label: "Comentarios", icon: MessageSquare },
            { k: "contact", label: "Mensajes", icon: Inbox },
            ...(user?.role === "admin" ? [{ k: "users", label: "Usuarios", icon: Shield }] : []),
            { k: "settings", label: "Configuración", icon: Settings },
          ].map(({ k, label, icon: Icon }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-tab-${k}`}
              className="px-4 py-3 text-xs uppercase tracking-widest font-bold flex items-center gap-2 whitespace-nowrap relative transition"
              style={{ color: tab === k ? "var(--accent)" : "var(--text-muted)" }}
            >
              <Icon size={14} />
              {label}
              {tab === k && (
                <span className="absolute left-2 right-2 -bottom-[1px] h-[3px]" style={{ background: "var(--accent)" }} />
              )}
            </button>
          ))}
        </div>

        {tab === "articles" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-headline uppercase text-3xl md:text-4xl">Artículos</h1>
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
                  {articlesTotal} en total {(articlesQuery.q || articlesQuery.category || articlesQuery.status || articlesQuery.date_range) && "(filtrado)"}
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setCuratorPrefill(null); setShowCurator(true); }} className="btn-ghost flex items-center gap-1" data-testid="admin-open-curator">
                  <Sparkles size={14} /> POPU Curador (IA)
                </button>
                <button onClick={openNew} className="btn-accent" data-testid="admin-new-article">
                  <Plus size={16} /> Nueva nota
                </button>
              </div>
            </div>

            {/* Search + filters */}
            <div className="mb-4 grid grid-cols-1 md:grid-cols-12 gap-2">
              <input
                type="text"
                placeholder="Buscar por título, slug o contenido..."
                value={articlesQuery.q}
                onChange={(e) => { setArticlesPage(0); setArticlesQuery({ ...articlesQuery, q: e.target.value }); }}
                className="md:col-span-5 px-3 py-2 border rounded-sm bg-transparent text-sm"
                style={{ borderColor: "var(--border)" }}
                data-testid="admin-articles-search"
              />
              <select
                value={articlesQuery.category}
                onChange={(e) => { setArticlesPage(0); setArticlesQuery({ ...articlesQuery, category: e.target.value }); }}
                className="md:col-span-2 px-3 py-2 border rounded-sm bg-transparent text-sm"
                style={{ borderColor: "var(--border)" }}
                data-testid="admin-articles-category"
              >
                <option value="">Todas las categorías</option>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
              <select
                value={articlesQuery.status}
                onChange={(e) => { setArticlesPage(0); setArticlesQuery({ ...articlesQuery, status: e.target.value }); }}
                className="md:col-span-2 px-3 py-2 border rounded-sm bg-transparent text-sm"
                style={{ borderColor: "var(--border)" }}
                data-testid="admin-articles-status"
              >
                <option value="">Todos los estados</option>
                <option value="published">Publicado</option>
                <option value="draft">Borrador</option>
              </select>
              <select
                value={articlesQuery.date_range}
                onChange={(e) => { setArticlesPage(0); setArticlesQuery({ ...articlesQuery, date_range: e.target.value }); }}
                className="md:col-span-2 px-3 py-2 border rounded-sm bg-transparent text-sm"
                style={{ borderColor: "var(--border)" }}
                data-testid="admin-articles-daterange"
              >
                <option value="">Cualquier fecha</option>
                <option value="today">Hoy</option>
                <option value="week">Última semana</option>
                <option value="month">Último mes</option>
                <option value="year">Último año</option>
              </select>
              {(articlesQuery.q || articlesQuery.category || articlesQuery.status || articlesQuery.date_range) && (
                <button
                  onClick={() => { setArticlesPage(0); setArticlesQuery({ q: "", category: "", status: "", date_range: "" }); }}
                  className="md:col-span-1 px-3 py-2 border rounded-sm text-sm hover:accent-text"
                  style={{ borderColor: "var(--border)" }}
                  data-testid="admin-articles-clear-filters"
                >
                  Limpiar
                </button>
              )}
            </div>

        <div className="border rounded-sm overflow-hidden" style={{ borderColor: "var(--border)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                <th className="text-left p-3 overline">Título</th>
                <th className="text-left p-3 overline">Categoría</th>
                <th className="text-left p-3 overline">Highlight</th>
                <th className="text-left p-3 overline">Estado</th>
                <th className="text-left p-3 overline">Publicado</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {articles.map((a) => (
                <tr key={a.id} className="border-b hover:bg-[color:var(--surface)]" style={{ borderColor: "var(--border)" }} data-testid={`admin-row-${a.slug}`}>
                  <td className="p-3 max-w-xs">
                    <div className="flex items-center gap-2">
                      {a.featured_image && <img src={a.featured_image} alt="" className="w-10 h-10 object-cover rounded-sm" />}
                      <div className="font-semibold line-clamp-1">{a.title}</div>
                    </div>
                  </td>
                  <td className="p-3 uppercase text-xs tracking-widest accent-text">{a.category_slug}</td>
                  <td className="p-3 text-xs uppercase">{a.highlight_level}</td>
                  <td className="p-3 text-xs uppercase">{a.status}</td>
                  <td className="p-3 text-xs" style={{ color: "var(--text-muted)" }}>{timeAgo(a.published_at)}</td>
                  <td className="p-3 flex gap-2 justify-end">
                    <button onClick={() => openEdit(a)} className="p-2 hover:accent-text" data-testid={`admin-edit-${a.slug}`}><Edit size={15} /></button>
                    <button onClick={() => remove(a)} className="p-2 hover:text-red-600" data-testid={`admin-delete-${a.slug}`}><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {articlesTotal > ARTICLES_PER_PAGE && (
          <div className="flex items-center justify-between mt-4 text-sm" data-testid="admin-articles-pagination">
            <p style={{ color: "var(--text-muted)" }}>
              Mostrando {articlesPage * ARTICLES_PER_PAGE + 1}–{Math.min((articlesPage + 1) * ARTICLES_PER_PAGE, articlesTotal)} de {articlesTotal}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setArticlesPage((p) => Math.max(0, p - 1))}
                disabled={articlesPage === 0}
                className="px-3 py-1.5 border rounded-sm hover:accent-text disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ borderColor: "var(--border)" }}
                data-testid="admin-articles-prev"
              >
                ← Anterior
              </button>
              <span className="px-3 py-1.5 text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                Página {articlesPage + 1} de {Math.ceil(articlesTotal / ARTICLES_PER_PAGE)}
              </span>
              <button
                onClick={() => setArticlesPage((p) => p + 1)}
                disabled={(articlesPage + 1) * ARTICLES_PER_PAGE >= articlesTotal}
                className="px-3 py-1.5 border rounded-sm hover:accent-text disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ borderColor: "var(--border)" }}
                data-testid="admin-articles-next"
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}
          </>
        )}

        {tab === "opinion" && <AdminOpinionTab />}
        {tab === "polls" && <AdminPollsTab />}
        {tab === "catalog" && <AdminCatalogTab />}
        {tab === "shortsfeed" && <AdminShortsFeedTab />}
        {tab === "analytics" && <AdminAnalyticsTab />}
        {tab === "ads" && <AdminAdsTab />}

        {tab === "captured" && (
          <AdminCapturedTab
            categories={categories}
            onPromote={(prefill) => {
              setCuratorPrefill(prefill);
              setShowCurator(true);
            }}
          />
        )}
        {tab === "sources" && <AdminSourcesTab categories={categories} />}
        {tab === "pages" && <AdminPagesTab />}
        {tab === "comments" && <AdminCommentsTab />}
        {tab === "contact" && <AdminContactTab />}
        {tab === "users" && user?.role === "admin" && <AdminUsersTab currentUser={user} />}
        {tab === "settings" && <AdminSettingsTab />}
      </main>

      {/* Article form modal */}
      {showForm && editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 px-4" style={{ background: "rgba(0,0,0,0.6)" }} data-testid="admin-form-modal">
          <form onSubmit={save} className="w-full max-w-3xl rounded-sm border max-h-[90vh] overflow-y-auto" style={{ background: "var(--bg)", borderColor: "var(--border)" }}>
            <div className="flex items-center justify-between p-5 border-b sticky top-0" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
              <h2 className="font-headline uppercase text-xl">
                {editing.id ? "Editar nota" : "Nueva nota"}
              </h2>
              <button type="button" onClick={closeForm}><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="overline block mb-1">Volanta</label>
                <input className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={editing.volanta || ""} onChange={(e) => setEditing({ ...editing, volanta: e.target.value })} data-testid="form-volanta" />
              </div>
              <div>
                <label className="overline block mb-1">Título *</label>
                <input required className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="form-title" />
              </div>
              <div>
                <label className="overline block mb-1">Bajada</label>
                <textarea className="w-full px-3 py-2 border rounded-sm bg-transparent min-h-[60px]" style={{ borderColor: "var(--border)" }} value={editing.bajada} onChange={(e) => setEditing({ ...editing, bajada: e.target.value })} data-testid="form-bajada" />
              </div>
              <div>
                <label className="overline block mb-1">Cuerpo</label>
                <RichEditor
                  value={editing.body || ""}
                  onChange={(html) => setEditing({ ...editing, body: html })}
                  placeholder="Escribí la nota acá..."
                  minHeight={280}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-1">Categoría</label>
                  <select className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={editing.category_slug} onChange={(e) => setEditing({ ...editing, category_slug: e.target.value })} data-testid="form-category">
                    {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline block mb-1">Highlight</label>
                  <select className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={editing.highlight_level} onChange={(e) => setEditing({ ...editing, highlight_level: e.target.value })} data-testid="form-highlight">
                    <option value="normal">Normal</option>
                    <option value="featured">Destacada</option>
                    <option value="hero">Hero</option>
                    <option value="urgent">Urgente</option>
                    <option value="trend">Tendencia</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="overline block mb-1">URL imagen destacada</label>
                <input className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={editing.featured_image || ""} onChange={(e) => setEditing({ ...editing, featured_image: e.target.value })} data-testid="form-image" placeholder="https://..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-1">Tags (separados por coma)</label>
                  <input className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={Array.isArray(editing.tags) ? editing.tags.join(", ") : editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} data-testid="form-tags" />
                </div>
                <div>
                  <label className="overline block mb-1">Min de lectura</label>
                  <input type="number" min="1" className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={editing.read_minutes || 2} onChange={(e) => setEditing({ ...editing, read_minutes: parseInt(e.target.value) || 2 })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-1">Fuente externa (opcional)</label>
                  <input className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={editing.source_name || ""} onChange={(e) => setEditing({ ...editing, source_name: e.target.value })} placeholder="Nombre" />
                </div>
                <div>
                  <label className="overline block mb-1">URL fuente</label>
                  <input className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={editing.source_url || ""} onChange={(e) => setEditing({ ...editing, source_url: e.target.value })} placeholder="https://..." />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.is_breaking} onChange={(e) => setEditing({ ...editing, is_breaking: e.target.checked })} data-testid="form-is-breaking" />
                ⚡ ÚLTIMO MOMENTO (breaking news, banner rojo en home)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.is_short} onChange={(e) => setEditing({ ...editing, is_short: e.target.checked })} data-testid="form-is-short" />
                Es un POPU Short
              </label>
              {editing.is_short && (
                <div className="grid grid-cols-3 gap-4 p-3 rounded-sm" style={{ background: "var(--surface)" }}>
                  <div>
                    <label className="overline block mb-1">Plataforma</label>
                    <select
                      className="w-full px-3 py-2 border rounded-sm bg-transparent"
                      style={{ borderColor: "var(--border)" }}
                      value={editing.video_provider || "tiktok"}
                      onChange={(e) => setEditing({ ...editing, video_provider: e.target.value })}
                      data-testid="form-video-provider"
                    >
                      <option value="tiktok">TikTok</option>
                      <option value="instagram">Instagram Reel</option>
                      <option value="youtube">YouTube Short</option>
                      <option value="mp4">MP4 directo</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="overline block mb-1">URL embed</label>
                    <input
                      className="w-full px-3 py-2 border rounded-sm bg-transparent"
                      style={{ borderColor: "var(--border)" }}
                      value={editing.video_embed_url || ""}
                      onChange={(e) => setEditing({ ...editing, video_embed_url: e.target.value })}
                      placeholder="https://www.tiktok.com/embed/v2/VIDEO_ID  ·  https://www.instagram.com/reel/CODE/embed/"
                      data-testid="form-video-embed-url"
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="overline block mb-1">Duración</label>
                    <input
                      className="w-32 px-3 py-2 border rounded-sm bg-transparent"
                      style={{ borderColor: "var(--border)" }}
                      value={editing.duration || ""}
                      onChange={(e) => setEditing({ ...editing, duration: e.target.value })}
                      placeholder="0:28"
                    />
                  </div>
                </div>
              )}
              <div className="flex gap-2 justify-end pt-3 border-t" style={{ borderColor: "var(--border)" }}>
                <button type="button" className="btn-ghost" onClick={closeForm}>Cancelar</button>
                <button type="submit" className="btn-accent" data-testid="form-submit">Guardar</button>
              </div>
            </div>
          </form>
        </div>
      )}

      {showCurator && (
        <CuratorModal
          onClose={() => { setShowCurator(false); setCuratorPrefill(null); }}
          onCreated={() => { setShowCurator(false); setCuratorPrefill(null); loadAll(); }}
          categories={categories}
          prefill={curatorPrefill}
        />
      )}
    </div>
  );
}

function CuratorModal({ onClose, onCreated, categories, prefill }) {
  const [sourceUrl, setSourceUrl] = useState(prefill?.source_url || "");
  const [sourceName, setSourceName] = useState(prefill?.source_name || "");
  const [rawText, setRawText] = useState(prefill?.raw_text || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [category, setCategory] = useState(prefill?.category_slug || "actualidad");
  const [image, setImage] = useState(prefill?.featured_image || "");
  const [capturedItemId] = useState(prefill?.item_id || null);

  const rewrite = async () => {
    if (!rawText.trim()) { toast.error("Pegá el texto original"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/curator/rewrite", { source_url: sourceUrl, source_name: sourceName, raw_text: rawText });
      if (r.data.parsed) {
        setResult(r.data.parsed);
        toast.success("Reescritura lista. Revisá y publicá.");
      } else {
        toast.error("La IA no devolvió formato esperado. Probá de nuevo.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error de IA");
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    try {
      await api.post("/admin/articles", {
        title: result.title,
        bajada: result.bajada,
        body: result.body,
        category_slug: category,
        tags: result.tags || [],
        featured_image: image,
        content_type: "curated",
        source_name: sourceName,
        source_url: sourceUrl,
        status: "published",
        highlight_level: "normal",
      });
      // mark captured item as promoted if it came from there
      if (capturedItemId) {
        try { await api.put(`/admin/captured/${capturedItemId}/status`, { status: "promoted" }); } catch (err) { console.warn("[popu]", err); }
      }
      toast.success("Publicado");
      onCreated();
    } catch (e) {
      toast.error("Error publicando");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 px-4" style={{ background: "rgba(0,0,0,0.7)" }} data-testid="curator-modal">
      <div className="w-full max-w-3xl rounded-sm border max-h-[90vh] overflow-y-auto" style={{ background: "var(--bg)", borderColor: "var(--border)" }}>
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--border)" }}>
          <h2 className="font-headline uppercase text-xl flex items-center gap-2"><Sparkles size={20} className="accent-text" /> POPU Curador IA</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            Pegá una nota externa y la IA la reescribe en tono POPU manteniendo la fuente. Ideal para curar contenido rápido.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <input placeholder="URL original" className="px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} data-testid="curator-source-url" />
            <input placeholder="Nombre fuente" className="px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={sourceName} onChange={(e) => setSourceName(e.target.value)} data-testid="curator-source-name" />
          </div>
          <textarea placeholder="Pegá el texto original aquí" className="w-full px-3 py-2 border rounded-sm bg-transparent min-h-[180px]" style={{ borderColor: "var(--border)" }} value={rawText} onChange={(e) => setRawText(e.target.value)} data-testid="curator-raw-text" />
          <button onClick={rewrite} disabled={busy} className="btn-accent disabled:opacity-60" data-testid="curator-rewrite">
            <Sparkles size={14} /> {busy ? "Reescribiendo..." : "Reescribir con IA"}
          </button>

          {result && (
            <div className="mt-4 border-t pt-4 space-y-3" style={{ borderColor: "var(--border)" }}>
              <p className="overline accent-text">VERSIÓN POPU</p>
              <input className="w-full px-3 py-2 border rounded-sm bg-transparent font-bold" style={{ borderColor: "var(--border)" }} value={result.title} onChange={(e) => setResult({ ...result, title: e.target.value })} />
              <textarea className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={result.bajada} onChange={(e) => setResult({ ...result, bajada: e.target.value })} />
              <textarea className="w-full px-3 py-2 border rounded-sm bg-transparent min-h-[180px] font-mono text-sm" style={{ borderColor: "var(--border)" }} value={result.body} onChange={(e) => setResult({ ...result, body: e.target.value })} />
              <div className="grid grid-cols-2 gap-4">
                <select className="px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={category} onChange={(e) => setCategory(e.target.value)}>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
                <input placeholder="URL imagen destacada" className="px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={image} onChange={(e) => setImage(e.target.value)} />
              </div>
              <button onClick={publish} className="btn-accent" data-testid="curator-publish">Publicar como nota curada</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
